import producingSegmentTiers from 'enums/producing_segment_tiers.json';
import unmeasurableReasons from 'enums/unmeasurable_reasons.json';

// Which piece of the well the percent is measured on: the tier of
// Query::WellProducingLengthLookup that answered
// (Query::UnitWellAssignmentSegmentShares::Measurement), and its label. The
// CSV export reads the same file.
export const TIER_LABELS = producingSegmentTiers;

export type ProducingSegmentTier = keyof typeof TIER_LABELS;

// Why a row with a producing segment has no percent; shared with the CSV too.
export const UNMEASURABLE_REASON_LABELS = unmeasurableReasons;

export type UnmeasurableReason = keyof typeof UNMEASURABLE_REASON_LABELS;

export type AssignmentType = 'unknown' | 'best_fit' | 'explicit';

// development_areas.unit_agreement_type.
export type UnitAgreementType = 'd_and_s' | 'joa';

// One of the well's other current assignments.
export interface OtherUnit {
  name: string;
  assignmentType: AssignmentType;
}

export const ASSIGNMENT_TYPE_LABELS: Record<AssignmentType, string> = {
  explicit: 'Explicit',
  best_fit: 'Best fit',
  unknown: 'Unknown',
};

export interface UnitWellAssignmentRow extends Record<string, unknown> {
  id: number;
  wellUWI: string;
  wellName: string | null;
  wellStatus: string | null;
  currentOperator: string | null;
  developmentAreaName: string;
  assignmentType: AssignmentType;
  tier: ProducingSegmentTier;
  producingLengthFt: number | null;
  lengthInUnitFt: number | null;
  // 0-100. Null when tier is 'none' (no producing segment), or when the segment
  // could not be measured against the unit (unmeasurableReason says why).
  percentInUnit: number | null;
  unmeasurableReason: UnmeasurableReason | null;
  unitAgreementType: UnitAgreementType | null;
  // How many current assignments the well has, this one included; more than 1
  // flags the row, with the well's other units and their types.
  wellAssignmentCount: number;
  otherUnits: OtherUnit[];
  // A best_fit row whose well also has an explicit row in another unit.
  bestFitSuperseded: boolean;
  wellHref: string;
  developmentAreaHref: string;
}

export interface UnitWellAssignmentsResponse {
  data: UnitWellAssignmentRow[];
  stats: {
    total: number;
  };
  refresh: RefreshStatus;
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
  };
}

export interface UnitWellAssignmentFilters {
  wellUWI: string;
  developmentAreaName: string;
  currentOperator: string;
  wellStatus: string;
  assignmentType: string;
  tier: string;
  percentBelow: string;
  // Only the rows whose well has more than one current assignment.
  multipleAssignments: boolean;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

// The refresh as the server records it (GET .../refresh_status, and the page's
// `refresh`): whether one is running, when the stored measurements were taken
// (null before the first refresh), and the last failed refresh, if any.
export interface RefreshStatus {
  refreshing: boolean;
  lastRefreshedAt: string | null;
  lastFailedAt: string | null;
  lastFailure: string | null;
}

// POST /admin/unit_well_assignments/refresh: queued, or refused because one is
// already running (409). A refusal for any other reason carries `error`.
export interface RefreshResponse {
  refreshing: boolean;
  queued: boolean;
}
